import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import {
  useHazardMaterialCategoryStore,
  useLocationStore,
  useUnitsofMeasureStore,
} from "../../../store/masterDataStore";
import { useHazardousMaterialStore } from "../../../store/hazardousMaterialStore";
import { HazardousMaterialType } from "../../../types/api";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const emptyForm: HazardousMaterialType = {
  materialName: "",
  description: "",
  materialCategory: "",
  quantity: "",
  storageLocation: "",
  unitOfMeasure: "",
  casNumber: "",
  batchNumber: "",
  supplier: "",
  expiryDate: "",
  safetyDataSheetName: "",
  safetyDataSheet: null,
};

const MaterialInventoryManagement: React.FC = () => {
  const [rowData, setRowData] = useState<HazardousMaterialType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<HazardousMaterialType>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);

  const { allLocations, allLocationsMap, fetchAllLocations } =
    useLocationStore();
  const {
    hazardMaterialCategory,
    hazardMaterialCategoryMap,
    fetchAllHazardMaterialCategory,
  } = useHazardMaterialCategoryStore();
  const { unitsofMeasure, unitsofMeasureMap, fetchAllUnitsofMeasure } =
    useUnitsofMeasureStore();
  const {
    hazardousMaterials,
    loading,
    fetchAllHazardousMaterials,
    createHazardousMaterial,
    updateHazardousMaterial,
    deleteHazardousMaterial,
    materialDataMap,
  } = useHazardousMaterialStore();

  // Fetch data on mount
  useEffect(() => {
    fetchAllHazardousMaterials();
    if (!allLocations.length) fetchAllLocations();
    if (!hazardMaterialCategory.length) fetchAllHazardMaterialCategory();
    if (!unitsofMeasure.length) fetchAllUnitsofMeasure();
  }, [
    fetchAllHazardousMaterials,
    fetchAllLocations,
    fetchAllHazardMaterialCategory,
    fetchAllUnitsofMeasure,
  ]);

  useEffect(() => {
    setRowData(hazardousMaterials ?? []);
  }, [hazardousMaterials]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    let success = false;
    if (isEditMode && editId) {
      success = await updateHazardousMaterial(formData, editId);
    } else {
      success = await createHazardousMaterial(formData);
    }
    if (success) {
      setModalOpen(false);
      setFormData(emptyForm);
      setEditId(null);
      setIsEditMode(false);
    }
  };

  const handleEdit = (data: HazardousMaterialType) => {
    setIsEditMode(true);
    setFormData({
      materialName: data.materialName || "",
      description: data.description || "",
      materialCategory: data.materialCategory || "",
      unitOfMeasure: data.unitOfMeasure || "",
      quantity: data.quantity || "",
      storageLocation: data.storageLocation || "",
      casNumber: data.casNumber || "",
      batchNumber: data.batchNumber || "",
      supplier: data.supplier || "",
      expiryDate: data.expiryDate || "",
      safetyDataSheetName: data.safetyDataSheetName || "",
      safetyDataSheet: data.safetyDataSheet || null,
    });
    setEditId(data._id || "");
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this hazardous material?"
    );
    if (confirmed) {
      await deleteHazardousMaterial(id);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setFormData(emptyForm);
    setEditId(null);
    setIsEditMode(false);
  };

  const columnDefs = useMemo<any>(
    () => [
      {
        field: "materialName",
        headerName: "Material Name",
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 10px", textAlign: "left" },
        valueGetter: (params: any) =>
          materialDataMap.get(params.data._id) || "",
      },
      {
        field: "materialCategory",
        headerName: "Material Category",
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 10px", textAlign: "left" },
        valueFormatter: (params: any) =>
          hazardMaterialCategoryMap.get(params.data.materialCategory) || "",
      },
      {
        field: "storageLocation",
        headerName: "Storage Location",
        minWidth: 150,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 10px", textAlign: "left" },
        valueFormatter: (params: any) => {
          return allLocationsMap.get(params.data.storageLocation) || "";
        },
      },
      {
        field: "casNumber",
        headerName: "CAS Number",
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 10px", textAlign: "left" },
      },
      {
        field: "quantity",
        headerName: "Quantity",
        minWidth: 50,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 10px", textAlign: "left" },
      },
      {
        field: "unitOfMeasure",
        headerName: "Unit of Measure",
        minWidth: 60,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { padding: "10px 10px", textAlign: "left" },
        valueFormatter: (params: any) =>
          unitsofMeasureMap.get(params.data.unitOfMeasure) || "",
      },
      {
        field: "action",
        headerName: "Action",
        cellStyle: { padding: "10px 15px 0 0", textAlign: "left" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        minWidth: 90,
        maxWidth: 110,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit Hazardous Material"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete Hazardous Material"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
      },
    ],
    [
      materialDataMap,
      allLocationsMap,
      unitsofMeasureMap,
      hazardMaterialCategoryMap,
    ]
  );

  const defaultColDef = useMemo<any>(
    () => ({
      editable: false,
    }),
    []
  );

  const theme = useMemo<any>(() => gridTheme, []);

  return (
    <>
      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Hazardous Material Inventory</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData(emptyForm);
            }}
          >
            <Plus size={20} /> Add Material
          </button>
        </div>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination={true}
                  paginationPageSize={5}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No hazardous materials added yet
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        closeButton={false}
        modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      >
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
            {isEditMode
              ? "Update Hazardous Material"
              : "Add New Hazardous Material"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="hazardousMaterialForm"
            className="flex flex-col gap-6 pb-20"
          >
            {/* Grid layout for paired inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Material Name */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="materialName"
                  className="text-sm font-medium text-gray-700"
                >
                  Material Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="materialName"
                  name="materialName"
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.materialName}
                  onChange={handleChange}
                  placeholder="Enter material name"
                  required
                  aria-label="Material Name"
                />
              </div>

              {/* Material Category */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="materialCategory"
                  className="text-sm font-medium text-gray-700"
                >
                  Material Category <span className="text-red-500">*</span>
                </label>
                <select
                  id="materialCategory"
                  name="materialCategory"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.materialCategory}
                  onChange={handleChange}
                  required
                  aria-label="Material Category"
                >
                  <option value="">Select Category</option>
                  {hazardMaterialCategory.map((hc: any) => (
                    <option key={hc._id} value={hc._id}>
                      {hc.hazardMaterialCategoryName}
                    </option>
                  ))}
                </select>
              </div>

              {/* <div className="flex flex-col gap-2">
                <label
                  htmlFor="hazardClass"
                  className="text-sm font-medium text-gray-700"
                >
                  Hazard Class <span className="text-red-500">*</span>
                </label>
                <select
                  id="hazardClass"
                  name="hazardClass"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.hazardClass}
                  onChange={handleChange}
                  required
                  disabled={!formData.materialCategory}
                  aria-label="Hazard Class"
                >
                  <option value="">Select Category</option>
                  {formData.materialCategory &&
                    hazardMaterialCategory.map((hc: any) => (
                      <option key={hc._id} value={hc._id}>
                        {hc.hazardClass}
                    </option>
                  ))}
                </select>
              </div> */}

              {/* CAS Number */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="casNumber"
                  className="text-sm font-medium text-gray-700"
                >
                  CAS Number
                </label>
                <input
                  id="casNumber"
                  name="casNumber"
                  type="text"
                  pattern="^\d{2,7}-\d{2}-\d$"
                  title="CAS Number must be in the format XX-XX-X, digits only"
                  value={formData.casNumber}
                  onChange={handleChange}
                  placeholder="e.g., 50-00-0"
                  aria-label="CAS Number"
                  required
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                />
              </div>

              {/* Batch Number */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="batchNumber"
                  className="text-sm font-medium text-gray-700"
                >
                  Batch Number
                </label>
                <input
                  id="batchNumber"
                  name="batchNumber"
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.batchNumber}
                  onChange={handleChange}
                  placeholder="Batch Number"
                  aria-label="Batch Number"
                />
              </div>

              {/* Quantity */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="quantity"
                  className="text-sm font-medium text-gray-700"
                >
                  Quantity <span className="text-red-500">*</span>
                </label>
                <input
                  id="quantity"
                  name="quantity"
                  type="number"
                  min={1}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                  placeholder="Enter quantity"
                  aria-label="Quantity"
                />
              </div>

              {/* Unit of Measure */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="unitOfMeasure"
                  className="text-sm font-medium text-gray-700"
                >
                  Unit of Measure
                </label>
                <select
                  id="unitOfMeasure"
                  name="unitOfMeasure"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.unitOfMeasure}
                  onChange={handleChange}
                  aria-label="Unit of Measure"
                >
                  <option value="">Select Unit</option>
                  {unitsofMeasure.map((unit) => (
                    <option key={unit._id} value={unit._id}>
                      {unit.unitName}
                    </option>
                  ))}
                </select>
              </div>

              {/* Supplier */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="supplier"
                  className="text-sm font-medium text-gray-700"
                >
                  Supplier
                </label>
                <input
                  id="supplier"
                  name="supplier"
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.supplier}
                  onChange={handleChange}
                  placeholder="Supplier name"
                  aria-label="Supplier"
                />
              </div>

              {/* Expiry Date */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="expiryDate"
                  className="text-sm font-medium text-gray-700"
                >
                  Expiry Date
                </label>
                <input
                  id="expiryDate"
                  name="expiryDate"
                  type="date"
                  min={new Date().toISOString().split("T")[0]}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.expiryDate}
                  onChange={handleChange}
                  aria-label="Expiry Date"
                />
              </div>

              {/* Storage Location */}
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="storageLocation"
                  className="text-sm font-medium text-gray-700"
                >
                  Storage Location
                </label>
                <select
                  id="storageLocation"
                  name="storageLocation"
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  value={formData.storageLocation}
                  onChange={handleChange}
                  aria-label="Storage Location"
                >
                  <option value="">Select Storage Location</option>
                  {/* Populate from your storage location master */}
                  {allLocations.map((loc) => (
                    <option key={loc._id} value={loc._id}>
                      {loc.room} - {loc.zone}
                    </option>
                  ))}
                </select>
              </div>

              {/* Safety Data Sheet */}
              <div className="flex flex-col gap-2 col-span-2">
                <label
                  htmlFor="safetyDataSheet"
                  className="text-sm font-medium text-gray-700"
                >
                  Safety Data Sheet (PDF, max 10MB){" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  id="safetyDataSheet"
                  name="safetyDataSheet"
                  type="file"
                  accept="application/pdf"
                  aria-label="Safety Data Sheet"
                  // onChange={handleFileChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                />
                {formData.safetyDataSheetName && (
                  <span className="text-gray-600 mt-1">
                    {formData.safetyDataSheetName}
                  </span>
                )}
              </div>
            </div>

            {/* Description full width */}
            <div className="flex flex-col gap-2 mt-4">
              <label
                htmlFor="description"
                className="text-sm font-medium text-gray-700"
              >
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description or special instructions (optional)"
                rows={4}
                aria-label="Description"
              />
            </div>

            <div className="h-20" />
          </form>
        </div>

        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={handleModalClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="hazardousMaterialForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
          >
            {isEditMode ? "Update" : "Add"}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default MaterialInventoryManagement;
